// ── Service de Dashboard (núcleo/aplicação) ───────────────────────
// Agregações para as 9 abas. Recebe o repositório via porta (DI) — não
// conhece Supabase. Cada função valida entrada, delega as leituras ao
// repositório (que aplica o filtro de escopo por usuário) e faz as
// transformações puras aqui. RLS de Config/27 também filtra no banco;
// o filtro de escopo é defesa em profundidade + redução de payload.

import type { UserFilterContext } from '@utils';
import type { FunnelDataRow } from '@/types/dashboard';
import type {
  IDashboardRepository,
  FunnelStageRow,
  TeamProfile,
  ActStatRow,
  EnterpriseSaleRow,
  EnterpriseLeadStageCardRow,
} from '../ports/IDashboardRepository';

const isoToDate = (iso: string | null | undefined): string | null =>
  (iso ? String(iso).slice(0, 10) : null);

export interface DashboardParams {
  workspaceId: string;
  funnelId?: string | null;
  startISO?: string | null;
  endISO?: string | null;
  userCtx?: UserFilterContext | null;
  /** Restringe os dados aos membros de um time (Enterprise). */
  memberIds?: string[] | null;
  /** "Agora" para cálculo de tempo de contato (default: relógio do server). */
  nowISO?: string | null;
}

// ─── Tipos públicos de saída ────────────────────────────────────
interface TeamStatEntry {
  user: TeamProfile & { role?: string };
  receivedLeads: number;
  closerSales: number;
  sdrSales: number;
  closerRevenue: number;
  sdrRevenue: number;
  closerCommission: number;
  sdrCommission: number;
}

export interface ActivitiesStats {
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  overdue_today: ActStatRow[];
  done_today: number;
  total: number;
}

interface AcqSubOrigin { count: number; campaigns: Record<string, number> }
interface AcqChannel { count: number; subOrigins: Record<string, AcqSubOrigin> }
export interface AcqChannelBar { channel: string; count: number; percent: number }
export interface LeadAcquisitionData {
  tree: Record<string, AcqChannel>;
  total: number;
  byChannel: AcqChannelBar[];
}

interface EffContactByOrigin { total: number; effectiveContacts: number }
export interface EffectiveContactRow {
  origin: string;
  total: number;
  effectiveContacts: number;
  rate: number;
}

interface LossGroup { role: string; pipeline_name: string; reason: string; count: number }

export interface ForecastRow {
  stage_id: string;
  stage_name: string;
  color: string | null;
  cards: number;
  faturamento_estimado: number;
  probability: number;
  receita_ponderada: number;
}

interface TrackingCampaign { count: number; fat: number; utms: Record<string, number> }
interface TrackingSub { count: number; fat: number; campanhas: Record<string, TrackingCampaign> }
interface TrackingChannel { count: number; fat: number; subs: Record<string, TrackingSub> }

export interface EnterpriseBreakdownRow {
  label: string;
  clients: number;
  lives: number;
  revenue: number;
}

export interface EnterpriseStageRow extends EnterpriseBreakdownRow {
  stageId: string | null;
  /** Tempo médio (dias) desde a entrada do card até agora, na etapa. */
  avgContactDays: number;
}

interface EnterpriseStageAccumulator extends EnterpriseStageRow {
  leadIds: Set<string>;
  ageDaysSum: number;
  ageDaysCount: number;
}

export interface EnterpriseTeamBreakdownRow {
  teamId: string;
  teamName: string;
  clients: number;
  lives: number;
  revenue: number;
}

interface EnterpriseTeamAccumulator {
  teamId: string;
  teamName: string;
  lives: number;
  revenue: number;
  leadIds: Set<string>;
}

export interface EnterpriseDashboardData {
  clients: number;
  lives: number;
  revenue: number;
  individualLives: number;
  individualClients: number;
  individualRevenue: number;
  collectiveLives: number;
  /** Snapshot do pipe vivo: vidas em cards abertos (status='active'). */
  negotiationLives: number;
  negotiationClients: number;
  negotiationRevenue: number;
  stages: EnterpriseStageRow[];
  /** Faturamento e vidas por equipe (sempre a empresa toda; independe de escopo). */
  teamBreakdown: EnterpriseTeamBreakdownRow[];
}

// Probabilidade de fallback quando a etapa não tem win_probability.
const FALLBACK_PROB_BY_NAME = (name: string | null): number => {
  const n = (name || '').toLowerCase();
  if (/reuni.*agendada|^agendada/.test(n)) return 0.20;
  if (/proposta/.test(n)) return 0.40;
  if (/follow.*up/.test(n)) return 0.60;
  if (/fechamento|negocia/.test(n)) return 0.80;
  if (/^venda|^ganho/.test(n)) return 1.00;
  return 0.10;
};

export class DashboardService {
  constructor(private readonly repo: IDashboardRepository) {}

  // ┌────────────────────────────────────────────────────────────┐
  // │  1) getFunnelData                                           │
  // └────────────────────────────────────────────────────────────┘
  getFunnelData = async ({
    workspaceId, funnelId = null, startISO, endISO, userCtx,
  }: DashboardParams): Promise<{ stages: FunnelStageRow[]; rows: FunnelDataRow[]; total_cards?: number }> => {
    if (!workspaceId) return { stages: [], rows: [] };
    const pipelineIds = await this.repo.getPipelineIds({ workspaceId, funnelId });
    if (pipelineIds.length === 0) return { stages: [], rows: [], total_cards: 0 };

    const stages = await this.repo.getFunnelStages(pipelineIds);
    const orderedStages = stages.slice().sort((a, b) => {
      const roleA = a.pipeline?.role === 'sdr' ? 0 : 1;
      const roleB = b.pipeline?.role === 'sdr' ? 0 : 1;
      if (roleA !== roleB) return roleA - roleB;
      const pipeCmp = (a.pipeline?.name || '').localeCompare(b.pipeline?.name || '');
      if (pipeCmp !== 0) return pipeCmp;
      return (a.order_index || 0) - (b.order_index || 0);
    });

    const cards = await this.repo.getFunnelCards({ pipelineIds, startISO, endISO, userCtx });

    const counts: Record<string, number> = {};
    for (const c of cards) {
      counts[c.stage_id ?? ''] = (counts[c.stage_id ?? ''] || 0) + 1;
    }

    const prevCountByPipeline: Record<string, number> = {};
    const rows: FunnelDataRow[] = orderedStages.map(s => {
      const count = counts[s.id] || 0;
      const pipeKey = s.pipeline_id ?? '';
      const prevCount = prevCountByPipeline[pipeKey] ?? null;
      const conversionFromPrev = (prevCount === null || prevCount === 0)
        ? null
        : count / prevCount;
      prevCountByPipeline[pipeKey] = count;
      return {
        stage_id: s.id,
        stage_name: s.name,
        pipeline_id: s.pipeline_id,
        pipeline_name: s.pipeline?.name || 'Pipeline',
        pipeline_role: s.pipeline?.role || null,
        color: s.color,
        order_index: s.order_index,
        count,
        conversion_from_prev: conversionFromPrev,
      };
    });

    return { stages: orderedStages, rows, total_cards: cards.length || 0 };
  };

  // ┌────────────────────────────────────────────────────────────┐
  // │  2) getTeamPerformance                                      │
  // └────────────────────────────────────────────────────────────┘
  getTeamPerformance = async ({
    workspaceId, startISO, endISO, userCtx,
  }: DashboardParams): Promise<Array<TeamStatEntry & { conversionRate: number | null }>> => {
    if (!workspaceId || !startISO || !endISO) return [];

    const timeIds = await this.repo.getActiveTimeIds(workspaceId);
    if (timeIds.length === 0) return [];

    const members = await this.repo.getTeamMembers({ timeIds, userCtx });

    const dedup = new Map<string, TeamProfile & { role?: string }>();
    for (const m of members) {
      const p = m.profile;
      if (!p || p.ativo === false) continue;
      if (dedup.has(p.id)) continue;
      dedup.set(p.id, { ...p, role: m.role });
    }
    const users = Array.from(dedup.values());
    if (users.length === 0) return [];

    const leadsRow = await this.repo.getLeadsSdrIds({ workspaceId, startISO, endISO });
    const leadsBySdr: Record<string, number> = {};
    for (const l of leadsRow) {
      if (!l.sdr_id) continue;
      leadsBySdr[l.sdr_id] = (leadsBySdr[l.sdr_id] || 0) + 1;
    }

    const startDate = isoToDate(startISO);
    const endDate = isoToDate(endISO);
    const comms = await this.repo.getCommissions({
      workspaceId,
      startDate: startDate ?? '',
      endDate: endDate ?? '',
    });

    const stats: Record<string, TeamStatEntry> = {};
    for (const u of users) stats[u.id] = {
      user: u,
      receivedLeads: leadsBySdr[u.id] || 0,
      closerSales: 0,
      sdrSales: 0,
      closerRevenue: 0,
      sdrRevenue: 0,
      closerCommission: 0,
      sdrCommission: 0,
    };
    for (const c of comms) {
      const closerId = c.card?.closer_id || c.closer_id;
      const sdrId    = c.card?.sdr_id    || c.sdr_id;
      const newCloserAsSdr = !sdrId || sdrId === closerId;
      let sdrComm = Number(c.sdr_commission) || 0;
      if (newCloserAsSdr !== !!c.closer_as_sdr) {
        const fat     = Number(c.faturamento) || 0;
        const sdrRate = Number(c.sdr_rate)    || 0;
        sdrComm = newCloserAsSdr ? 0 : fat * sdrRate;
      }
      if (closerId && stats[closerId]) {
        stats[closerId].closerSales += 1;
        stats[closerId].closerRevenue += Number(c.faturamento) || 0;
        stats[closerId].closerCommission += Number(c.closer_commission) || 0;
      }
      if (!newCloserAsSdr && sdrId && stats[sdrId]) {
        stats[sdrId].sdrSales += 1;
        stats[sdrId].sdrRevenue += Number(c.faturamento) || 0;
        stats[sdrId].sdrCommission += sdrComm;
      }
    }

    return Object.values(stats).map(s => ({
      ...s,
      conversionRate: s.receivedLeads > 0
        ? (s.closerSales / s.receivedLeads)
        : null,
    }));
  };

  // ┌────────────────────────────────────────────────────────────┐
  // │  3) getActivitiesStats                                      │
  // └────────────────────────────────────────────────────────────┘
  getActivitiesStats = async ({
    workspaceId, startISO, endISO, userCtx,
  }: DashboardParams): Promise<ActivitiesStats | null> => {
    if (!workspaceId) return null;

    const data = await this.repo.getActivities({ workspaceId, startISO, endISO, userCtx });

    const byStatus: Record<string, number> = { pending: 0, done: 0, cancelled: 0 };
    const byType: Record<string, number> = {};
    const overdueToday: ActStatRow[] = [];
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const now = new Date();

    for (const a of data) {
      if (a.status in byStatus) byStatus[a.status] += 1;
      byType[a.type] = (byType[a.type] || 0) + 1;
      if (a.status === 'pending' && a.due_date && new Date(a.due_date) < now) {
        overdueToday.push(a);
      }
    }

    const doneToday = await this.repo.getDoneTodayCount({
      workspaceId,
      startOfTodayISO: startOfToday.toISOString(),
      userCtx,
    });

    return {
      by_status: byStatus,
      by_type: byType,
      overdue_today: overdueToday,
      done_today: doneToday || 0,
      total: data.length || 0,
    };
  };

  // ┌────────────────────────────────────────────────────────────┐
  // │  4) getLeadAcquisitionData                                  │
  // └────────────────────────────────────────────────────────────┘
  getLeadAcquisitionData = async ({
    workspaceId, startISO, endISO, userCtx,
  }: DashboardParams): Promise<LeadAcquisitionData> => {
    if (!workspaceId) return { tree: {}, total: 0, byChannel: [] };

    const originsConfig = await this.repo.getWorkspaceOrigins(workspaceId);
    const canonicalChannels = Array.isArray(originsConfig?.canais) ? originsConfig.canais : [];

    const data = await this.repo.getAcquisitionLeads({ workspaceId, startISO, endISO, userCtx });

    const tree: Record<string, AcqChannel> = {};
    const byRawChannel: Record<string, number> = {};
    let total = 0;
    for (const l of data) {
      total += 1;
      const channel = l.canal || l.origem || '(sem canal)';
      const subOrigin = l.sub_origem || '(sem sub-origem)';
      const campaign = l.campanha || '(sem campanha)';
      if (!tree[channel]) tree[channel] = { count: 0, subOrigins: {} };
      tree[channel].count += 1;
      if (!tree[channel].subOrigins[subOrigin]) tree[channel].subOrigins[subOrigin] = { count: 0, campaigns: {} };
      tree[channel].subOrigins[subOrigin].count += 1;
      tree[channel].subOrigins[subOrigin].campaigns[campaign] = (tree[channel].subOrigins[subOrigin].campaigns[campaign] || 0) + 1;
      byRawChannel[channel] = (byRawChannel[channel] || 0) + 1;
    }

    let byChannel: AcqChannelBar[];
    if (canonicalChannels.length === 0) {
      byChannel = Object.entries(byRawChannel)
        .map(([channel, count]) => ({ channel, count, percent: total > 0 ? count / total : 0 }))
        .sort((a, b) => b.count - a.count);
    } else {
      const norm = (s: string | null | undefined): string => (s || '').toLowerCase().trim();
      const consumed = new Set<string>();
      byChannel = canonicalChannels.map((c) => {
        const target = norm(c.name);
        let count = 0;
        for (const [raw, n] of Object.entries(byRawChannel)) {
          if (consumed.has(raw)) continue;
          if (norm(raw) === target) {
            count += n;
            consumed.add(raw);
          }
        }
        return { channel: c.name, count, percent: total > 0 ? count / total : 0 };
      });
      let otherCount = 0;
      for (const [raw, n] of Object.entries(byRawChannel)) {
        if (!consumed.has(raw)) otherCount += n;
      }
      if (otherCount > 0) {
        byChannel.push({
          channel: 'Outros',
          count: otherCount,
          percent: total > 0 ? otherCount / total : 0,
        });
      }
    }

    return { tree, total, byChannel };
  };

  // ┌────────────────────────────────────────────────────────────┐
  // │  5) getEffectiveContactData                                 │
  // └────────────────────────────────────────────────────────────┘
  getEffectiveContactData = async ({
    workspaceId, startISO, endISO, userCtx,
  }: DashboardParams): Promise<{ rows: EffectiveContactRow[]; total: number; totalEffectiveContacts: number }> => {
    if (!workspaceId) return { rows: [], total: 0, totalEffectiveContacts: 0 };

    const leadsData = await this.repo.getEffectiveContactLeads({ workspaceId, startISO, endISO, userCtx });

    const effectiveContactStageIdList = await this.repo.getEffectiveContactStageIds(workspaceId);
    const effectiveContactStageIds = new Set(effectiveContactStageIdList);

    const historyData = await this.repo.getStageChangedHistory({ workspaceId, startISO, endISO });

    const effectiveContactLeadIds = new Set<string>();
    for (const h of historyData) {
      const toId = h.data?.to_stage_id;
      if (toId && effectiveContactStageIds.has(toId)) {
        if (h.lead_id) effectiveContactLeadIds.add(h.lead_id);
        continue;
      }
      if (effectiveContactStageIds.size === 0) {
        const toName = (h.data?.to_stage_name || '').toLowerCase();
        if (/em.*contato/.test(toName) && h.lead_id) effectiveContactLeadIds.add(h.lead_id);
      }
    }

    const byOrigin: Record<string, EffContactByOrigin> = {};
    for (const l of leadsData) {
      const key = l.canal || l.origem || '(sem origem)';
      if (!byOrigin[key]) byOrigin[key] = { total: 0, effectiveContacts: 0 };
      byOrigin[key].total += 1;
      if (effectiveContactLeadIds.has(l.id)) byOrigin[key].effectiveContacts += 1;
    }

    const rows: EffectiveContactRow[] = Object.entries(byOrigin).map(([origin, v]) => ({
      origin,
      total: v.total,
      effectiveContacts: v.effectiveContacts,
      rate: v.total > 0 ? v.effectiveContacts / v.total : 0,
    })).sort((a, b) => b.total - a.total);

    return {
      rows,
      total: leadsData.length || 0,
      totalEffectiveContacts: effectiveContactLeadIds.size,
    };
  };

  // ┌────────────────────────────────────────────────────────────┐
  // │  6) getLossReasonsData                                      │
  // └────────────────────────────────────────────────────────────┘
  getLossReasonsData = async ({
    workspaceId, startISO, endISO, funnelId = null, userCtx,
  }: DashboardParams): Promise<LossGroup[]> => {
    if (!workspaceId) return [];
    const pipelineIds = await this.repo.getPipelineIds({ workspaceId, funnelId });
    if (pipelineIds.length === 0) return [];

    const data = await this.repo.getLossCards({ pipelineIds, startISO, endISO, userCtx });
    const filtered = data.filter(c => c.pipeline);

    const grouped: Record<string, LossGroup> = {};
    for (const c of filtered) {
      const role = c.pipeline?.role || 'unknown';
      const reason = (c.loss_reason || '').trim() || '(sem motivo)';
      const key = `${role}::${reason}`;
      if (!grouped[key]) grouped[key] = { role, pipeline_name: c.pipeline?.name || '', reason, count: 0 };
      grouped[key].count += 1;
    }

    return Object.values(grouped).sort((a, b) => b.count - a.count);
  };

  // ┌────────────────────────────────────────────────────────────┐
  // │  7) getForecastData                                         │
  // └────────────────────────────────────────────────────────────┘
  getForecastData = async ({
    workspaceId, funnelId = null, userCtx,
  }: DashboardParams): Promise<{ rows: ForecastRow[]; totals: { count: number; fat_total: number; weighted: number } }> => {
    if (!workspaceId) return { rows: [], totals: { count: 0, fat_total: 0, weighted: 0 } };

    const closerPipeIds = await this.repo.getPipelineIds({ workspaceId, funnelId, role: 'closer' });
    if (closerPipeIds.length === 0) return { rows: [], totals: { count: 0, fat_total: 0, weighted: 0 } };

    const stages = await this.repo.getForecastStages(closerPipeIds);
    const cards = await this.repo.getForecastCards({ pipelineIds: closerPipeIds, userCtx });

    const cardsByStage: Record<string, typeof cards> = {};
    for (const c of cards) {
      const key = c.stage_id ?? '';
      if (!cardsByStage[key]) cardsByStage[key] = [];
      cardsByStage[key].push(c);
    }

    let count = 0, fatTotal = 0, weighted = 0;
    const rows: ForecastRow[] = stages.map(s => {
      const stageCards = cardsByStage[s.id] || [];
      const stageCount = stageCards.length;
      const stageFat = stageCards.reduce((acc, c) => acc + (Number(c.faturamento) || 0), 0);
      const prob = (s.win_probability != null) ? Number(s.win_probability) : FALLBACK_PROB_BY_NAME(s.name);
      const stageWeighted = stageFat * prob;

      count    += stageCount;
      fatTotal += stageFat;
      weighted += stageWeighted;

      return {
        stage_id: s.id,
        stage_name: s.name,
        color: s.color,
        cards: stageCount,
        faturamento_estimado: stageFat,
        probability: prob,
        receita_ponderada: stageWeighted,
      };
    });

    return { rows, totals: { count, fat_total: fatTotal, weighted } };
  };

  // ┌────────────────────────────────────────────────────────────┐
  // │  8) getTrackingData                                         │
  // └────────────────────────────────────────────────────────────┘
  getTrackingData = async ({
    workspaceId, startISO, endISO, userCtx,
  }: DashboardParams): Promise<{ tree: Record<string, TrackingChannel>; total: number; fat_total: number }> => {
    if (!workspaceId) return { tree: {}, total: 0, fat_total: 0 };

    const data = await this.repo.getTrackingLeads({ workspaceId, startISO, endISO, userCtx });

    const tree: Record<string, TrackingChannel> = {};
    let total = 0, fatTotal = 0;
    for (const l of data) {
      total += 1;
      const wonFat = (l.cards || [])
        .filter(c => c.status === 'won' && c.faturamento)
        .reduce((acc, c) => acc + Number(c.faturamento), 0);
      fatTotal += wonFat;

      const canal  = l.canal || '(sem canal)';
      const sub    = l.sub_origem || '(sem sub)';
      const camp   = l.campanha || '(sem campanha)';
      const utm    = [l.utm_source, l.utm_medium, l.utm_campaign, l.utm_content]
        .filter(Boolean).join(' / ') || '(sem utm)';

      if (!tree[canal]) tree[canal] = { count: 0, fat: 0, subs: {} };
      tree[canal].count += 1; tree[canal].fat += wonFat;
      if (!tree[canal].subs[sub]) tree[canal].subs[sub] = { count: 0, fat: 0, campanhas: {} };
      tree[canal].subs[sub].count += 1; tree[canal].subs[sub].fat += wonFat;
      if (!tree[canal].subs[sub].campanhas[camp]) tree[canal].subs[sub].campanhas[camp] = { count: 0, fat: 0, utms: {} };
      tree[canal].subs[sub].campanhas[camp].count += 1;
      tree[canal].subs[sub].campanhas[camp].fat   += wonFat;
      tree[canal].subs[sub].campanhas[camp].utms[utm] =
        (tree[canal].subs[sub].campanhas[camp].utms[utm] || 0) + 1;
    }

    return { tree, total, fat_total: fatTotal };
  };

  getEnterpriseTeams = (workspaceId: string) => this.repo.getEnterpriseTeams(workspaceId);

  // Vendas enterprise cruas (para a area de Clientes Ativos). O recorte por
  // data_venda + userCtx e feito no repo; a pagina calcula comissao/retencao.
  //
  // `crossWorkspace` e o modo do grupo enterprise: a carteira passa a ser a de
  // TODOS os workspaces do grupo (matriz + enterprise + aex) e sem recorte por
  // sdr/closer - todos veem todos. Fica opt-in porque o dashboard enterprise
  // usa a mesma leitura e as metas dele sao por workspace.
  getEnterpriseClientSales = (
    { workspaceId, startISO, endISO, userCtx }: DashboardParams,
    crossWorkspace = false,
  ): Promise<EnterpriseSaleRow[]> => {
    if (!workspaceId) return Promise.resolve([]);
    if (crossWorkspace) return this.repo.getEnterpriseGroupSales({ workspaceId, startISO, endISO });
    return this.repo.getEnterpriseSales({ workspaceId, startISO, endISO, userCtx });
  };

  getEnterpriseDashboardData = async ({
    workspaceId, startISO, endISO, userCtx, memberIds, nowISO,
  }: DashboardParams): Promise<EnterpriseDashboardData> => {
    if (!workspaceId) {
      return {
        clients: 0,
        lives: 0,
        revenue: 0,
        individualLives: 0,
        individualClients: 0,
        individualRevenue: 0,
        collectiveLives: 0,
        negotiationLives: 0,
        negotiationClients: 0,
        negotiationRevenue: 0,
        stages: [],
        teamBreakdown: [],
      };
    }

    const [sales, pipelineIds, teams] = await Promise.all([
      this.repo.getEnterpriseSales({ workspaceId, startISO, endISO, userCtx, memberIds }),
      this.repo.getPipelineIds({ workspaceId }),
      this.repo.getEnterpriseTeams(workspaceId),
    ]);
    const [leadStageCards, negotiationCards] = pipelineIds.length
      ? await Promise.all([
        this.repo.getEnterpriseLeadStageCards({ pipelineIds, startISO, endISO, userCtx, memberIds }),
        this.repo.getEnterpriseNegotiationCards({ pipelineIds, userCtx, memberIds }),
      ])
      : [[], []];
    const currentUserId = userCtx?.id || null;
    const nowMs = nowISO ? new Date(nowISO).getTime() : Date.now();

    const clients = new Set<string>();
    const stageMap: Record<string, EnterpriseStageAccumulator> = {};

    let lives = 0;
    let revenue = 0;
    let individualLives = 0;
    let individualRevenue = 0;
    const individualClientSet = new Set<string>();

    // Mapa usuario -> time (primeiro time em que aparece) para o breakdown.
    const userTeam = new Map<string, { teamId: string; teamName: string }>();
    for (const team of teams) {
      for (const uid of team.memberIds) {
        if (!userTeam.has(uid)) userTeam.set(uid, { teamId: team.id, teamName: team.name });
      }
    }
    const teamAcc: Record<string, EnterpriseTeamAccumulator> = {};

    const livesFromProposal = (proposta: unknown): number => {
      if (!proposta || typeof proposta !== 'object') return 0;
      const plans = (proposta as { plans?: unknown }).plans;
      if (!Array.isArray(plans)) return 0;
      return plans.reduce((sum, line) => {
        if (!line || typeof line !== 'object') return sum;
        // Adicional entra no faturamento, nunca na contagem de vidas. Linha sem
        // `kind` é proposta anterior ao catálogo v2, quando tudo era plano.
        if ((line as { kind?: unknown }).kind === 'addon') return sum;
        return sum + (Number((line as { quantity?: unknown }).quantity) || 0);
      }, 0);
    };

    const revenueFromProposal = (proposta: unknown): number => {
      if (!proposta || typeof proposta !== 'object') return 0;
      const plans = (proposta as { plans?: unknown }).plans;
      if (Array.isArray(plans)) {
        const fromPlans = plans.reduce((sum, line) => {
          if (!line || typeof line !== 'object') return sum;
          return sum + (Number((line as { total?: unknown; totalValue?: unknown; total_value?: unknown }).total)
            || Number((line as { totalValue?: unknown }).totalValue)
            || Number((line as { total_value?: unknown }).total_value)
            || 0);
        }, 0);
        if (fromPlans > 0) return fromPlans;
      }
      return Number((proposta as { valor?: unknown; value?: unknown }).valor)
        || Number((proposta as { value?: unknown }).value)
        || 0;
    };

    const livesFromSale = (sale: EnterpriseSaleRow): number => {
      const fromPlans = (sale.sale_plans || [])
        .filter(line => line.kind_snapshot !== 'addon')
        .reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
      if (fromPlans > 0) return fromPlans;
      return livesFromProposal(sale.lead?.proposta);
    };

    const ageInDays = (createdAt: string | null | undefined): number | null => {
      if (!createdAt) return null;
      const created = new Date(createdAt).getTime();
      if (!Number.isFinite(created)) return null;
      return Math.max(0, Math.round((nowMs - created) / 86_400_000));
    };

    const addBreakdown = (
      target: Record<string, EnterpriseStageAccumulator>,
      rawLabel: string | null | undefined,
      rowLives: number,
      rowRevenue: number,
      leadId: string,
      stageId: string | null = null,
      ageDays: number | null = null,
    ) => {
      const label = String(rawLabel || '').trim() || 'Nao informado';
      const key = stageId || label;
      if (!target[key]) target[key] = { label, stageId, clients: 0, lives: 0, revenue: 0, avgContactDays: 0, leadIds: new Set<string>(), ageDaysSum: 0, ageDaysCount: 0 };
      if (leadId) target[key].leadIds.add(leadId);
      target[key].clients = target[key].leadIds.size;
      target[key].lives += rowLives;
      target[key].revenue += rowRevenue;
      if (ageDays != null) {
        target[key].ageDaysSum += ageDays;
        target[key].ageDaysCount += 1;
      }
    };

    for (const sale of sales) {
      const saleLives = livesFromSale(sale);
      const saleRevenue = Number(sale.valor_total) || 0;
      lives += saleLives;
      revenue += saleRevenue;
      if (sale.lead_id) clients.add(sale.lead_id);

      const belongsToCurrentUser = !!currentUserId
        && (sale.card?.closer_id === currentUserId || sale.card?.sdr_id === currentUserId);
      if (belongsToCurrentUser) {
        individualLives += saleLives;
        individualRevenue += saleRevenue;
        if (sale.lead_id) individualClientSet.add(sale.lead_id);
      }

      // Atribui a venda a UM time: o do colaborador (sdr) e, se nao houver, o do comercial (closer).
      const teamOwner = (sale.card?.sdr_id && userTeam.get(sale.card.sdr_id))
        || (sale.card?.closer_id && userTeam.get(sale.card.closer_id))
        || null;
      if (teamOwner) {
        if (!teamAcc[teamOwner.teamId]) {
          teamAcc[teamOwner.teamId] = { teamId: teamOwner.teamId, teamName: teamOwner.teamName, lives: 0, revenue: 0, leadIds: new Set<string>() };
        }
        teamAcc[teamOwner.teamId].lives += saleLives;
        teamAcc[teamOwner.teamId].revenue += saleRevenue;
        if (sale.lead_id) teamAcc[teamOwner.teamId].leadIds.add(sale.lead_id);
      }
    }

    const addLeadStageCard = (card: EnterpriseLeadStageCardRow) => {
      const leadId = card.lead_id || card.lead?.id || card.id;
      const proposalLives = livesFromProposal(card.lead?.proposta);
      const proposalRevenue = revenueFromProposal(card.lead?.proposta);
      addBreakdown(
        stageMap,
        card.stage?.name || card.status || card.lead?.status || 'Sem etapa',
        proposalLives,
        Number(card.faturamento) || proposalRevenue,
        leadId,
        card.stage_id || card.stage?.id || null,
        ageInDays(card.created_at),
      );
    };

    for (const card of leadStageCards) {
      addLeadStageCard(card);
    }

    // Snapshot "vidas em negociação": cards abertos AGORA com proposta.
    const negotiationClientSet = new Set<string>();
    let negotiationLives = 0;
    let negotiationRevenue = 0;
    for (const card of negotiationCards) {
      const cardLives = livesFromProposal(card.lead?.proposta);
      negotiationLives += cardLives;
      negotiationRevenue += Number(card.faturamento) || revenueFromProposal(card.lead?.proposta);
      const leadId = card.lead_id || card.lead?.id || card.id;
      if (leadId) negotiationClientSet.add(leadId);
    }

    const sortRows = <T extends EnterpriseBreakdownRow>(rows: T[]): T[] =>
      rows.sort((a, b) => (b.lives - a.lives) || (b.clients - a.clients)).slice(0, 5);

    const cleanStageRows = Object.values(stageMap).map(row => ({
      label: row.label,
      stageId: row.stageId,
      clients: row.clients,
      lives: row.lives,
      revenue: row.revenue,
      avgContactDays: row.ageDaysCount > 0 ? Math.round(row.ageDaysSum / row.ageDaysCount) : 0,
    }));

    return {
      clients: clients.size,
      lives,
      revenue,
      individualLives,
      individualClients: individualClientSet.size,
      individualRevenue,
      collectiveLives: lives,
      negotiationLives,
      negotiationClients: negotiationClientSet.size,
      negotiationRevenue,
      stages: sortRows(cleanStageRows),
      teamBreakdown: Object.values(teamAcc)
        .map(t => ({ teamId: t.teamId, teamName: t.teamName, clients: t.leadIds.size, lives: t.lives, revenue: t.revenue }))
        .sort((a, b) => (b.lives - a.lives) || (b.revenue - a.revenue)),
    };
  };
}
